import { useModalForm } from '../../../../foundation/hooks';
import { useSearchPubMedIdMutation } from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import { PubMedPublication } from '../../redux';

interface FormValues {
  pubmed_id?: string;
}

interface UsePubMedModalLogicProps {
  closeModal: () => void;
  onPubMedSuccess: (pubmedPublication: PubMedPublication) => void;
}

/**
 * Custom hook that encapsulates all the business logic for PubMedModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const usePubMedModalLogic = ({
  closeModal,
  onPubMedSuccess,
}: UsePubMedModalLogicProps) => {
  // Mutation hook returns a [trigger, result] tuple.
  // This is a "search" mutation (POST to external PubMed API) rather than an
  // edit; the result is passed back to the caller via onPubMedSuccess
  const [searchPubMedId, { isLoading: isSearching }] =
    useSearchPubMedIdMutation();

  const { isLoading, setIsLoading, user, createModalOpts } = useModalForm({
    onClose: closeModal,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const modalOpts = {
    ...createModalOpts('Please enter a PubMed ID to add a new reference'),
  };

  const initialValues: FormValues = {
    pubmed_id: undefined,
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading || isSearching) {
      return;
    }

    if (user) {
      try {
        setIsLoading(true);

        const response = await searchPubMedId({
          requestData: {
            pubmed_id: values.pubmed_id,
          },
        }).unwrap();

        onPubMedSuccess(response);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        closeModal();
      }
    } else {
      // Unauthorized: open login modal (no redirect)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app:open-login'));
      }
      return;
    }
  };

  return {
    isLoading,
    user,
    modalOpts,
    initialValues,
    handleFormSubmit,
  };
};

export default usePubMedModalLogic;
